import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

const FRIENDLY: Record<string, { label: string; type: "string" | "boolean" | "number"; help?: string }> = {
  app_name: { label: "App name", type: "string", help: "Shown in the sidebar." },
  physical_address: { label: "Physical address (CAN-SPAM)", type: "string", help: "Required by law in US/EU. Appears in every campaign footer. Sends are blocked until this is set." },
  unsubscribe_footer_enabled: { label: "Inject unsubscribe footer", type: "boolean", help: "Off = rely on the email provider's auto-injected link only. Recommended on." },
  default_throttle_per_minute: { label: "Default throttle (per minute)", type: "number" },
  workspace_daily_cap: { label: "Workspace daily cap", type: "number", help: "Set this to your delivery provider's per-day limit. Sends pause at this number." },
  bounce_alert_threshold_pct: { label: "Bounce alert threshold (%)", type: "number" },
  complaint_alert_threshold_pct: { label: "Complaint alert threshold (%)", type: "number" },
  reply_to_pattern: { label: "Reply-To pattern", type: "string", help: "{user.email} = sender's wenmail address. Replies land in their webmail inbox." },
  otp_recipient: { label: "OTP recipient (gatekeeper)", type: "string", help: "ALL settings & invite OTPs go here, not to the actor." },
};

export default function SettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["settings"], queryFn: () => api<{ settings: Record<string, unknown> }>("/api/settings") });
  const [editing, setEditing] = useState<{ key: string; value: unknown } | null>(null);

  if (isLoading || !data) return <div className="p-8">Loading…</div>;

  const entries = Object.entries(data.settings).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="p-4 md:p-8 max-w-3xl">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Settings</h1>
      <p className="text-sm text-gray-500 mb-6">Workspace-wide. Every change requires an OTP confirmation sent to the gatekeeper.</p>

      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
        {entries.map(([key, value]) => {
          const meta = FRIENDLY[key];
          return (
            <div key={key} className="px-5 py-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="font-medium text-sm text-gray-900">{meta?.label ?? key}</div>
                {meta?.help && <p className="text-xs text-gray-500 mt-0.5">{meta.help}</p>}
                <div className="text-xs font-mono text-gray-700 mt-1 break-all">
                  {typeof value === "boolean" ? (value ? "true" : "false") : JSON.stringify(value)}
                </div>
              </div>
              <button onClick={() => setEditing({ key, value })}
                className="text-sm text-indigo-600 hover:underline shrink-0">Edit</button>
            </div>
          );
        })}
      </div>

      {editing && <EditModal initial={editing} onClose={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["settings"] }); }} />}
    </div>
  );
}

function EditModal({ initial, onClose }: { initial: { key: string; value: unknown }; onClose: () => void }) {
  const meta = FRIENDLY[initial.key];
  const [draft, setDraft] = useState(typeof initial.value === "boolean" ? String(initial.value) : String(initial.value ?? ""));
  const [otpId, setOtpId] = useState<string | null>(null);
  const [code, setCode] = useState("");

  const request = useMutation({
    mutationFn: () => {
      let value: unknown = draft;
      if (meta?.type === "boolean") value = draft === "true";
      else if (meta?.type === "number") value = Number(draft);
      return api<{ otpId: string }>("/api/settings/request-change", {
        method: "POST", body: JSON.stringify({ key: initial.key, value }),
      });
    },
    onSuccess: (r) => setOtpId(r.otpId),
  });

  const confirm = useMutation({
    mutationFn: () => api("/api/settings/confirm-change", { method: "POST", body: JSON.stringify({ otpId, code }) }),
    onSuccess: onClose,
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold text-gray-900 mb-1">{meta?.label ?? initial.key}</h3>
        <p className="text-xs text-gray-500 mb-4">{meta?.help}</p>

        {!otpId ? (
          <>
            {meta?.type === "boolean" ? (
              <select value={draft} onChange={(e) => setDraft(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mb-3">
                <option value="true">true</option><option value="false">false</option>
              </select>
            ) : (
              <input type={meta?.type === "number" ? "number" : "text"} value={draft} onChange={(e) => setDraft(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mb-3" />
            )}
            {request.error && <p className="text-sm text-red-600 mb-2">{(request.error as Error).message}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="text-sm text-gray-500 px-3 py-1.5">Cancel</button>
              <button onClick={() => request.mutate()} disabled={request.isPending}
                className="bg-indigo-600 text-white rounded px-3 py-1.5 text-sm hover:bg-indigo-700">
                {request.isPending ? "Sending OTP…" : "Send OTP"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-700 mb-3">A 6-digit code has been sent to the gatekeeper. Enter it to apply the change.</p>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" autoFocus
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-mono mb-3" />
            {confirm.error && <p className="text-sm text-red-600 mb-2">{(confirm.error as Error).message}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="text-sm text-gray-500 px-3 py-1.5">Cancel</button>
              <button onClick={() => confirm.mutate()} disabled={code.length < 4 || confirm.isPending}
                className="bg-indigo-600 text-white rounded px-3 py-1.5 text-sm hover:bg-indigo-700 disabled:opacity-50">
                {confirm.isPending ? "Applying…" : "Apply"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
